<template>
  <HrPageLayout
    title="Psychosocial Support"
    subtitle="Confidential counselling cases, session tracking, and case closure"
    icon="bx bx-heart"
    :breadcrumb-items="[{ label: 'HR Management' }, { label: 'Psychosocial' }]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-primary" @click="openCreate">
        <i class="bx bx-plus"></i> Open Case
      </button>
    </template>

    <div class="hr-metrics-grid hr-mb-4">
      <div class="hr-metric-card gradient-1"><div class="hr-metric-content">
              <p class="hr-metric-label">Total</p><h2 class="hr-metric-value">{{ metrics.total || 0 }}</h2>
            </div></div>
      <div class="hr-metric-card gradient-4"><div class="hr-metric-content">
              <p class="hr-metric-label">Open</p><h2 class="hr-metric-value">{{ metrics.open_count || 0 }}</h2>
            </div></div>
      <div class="hr-metric-card gradient-3"><div class="hr-metric-content">
              <p class="hr-metric-label">In progress</p><h2 class="hr-metric-value">{{ metrics.in_progress_count || 0 }}</h2>
            </div></div>
      <div class="hr-metric-card gradient-2"><div class="hr-metric-content">
              <p class="hr-metric-label">Closed</p><h2 class="hr-metric-value">{{ metrics.closed_count || 0 }}</h2>
            </div></div>
    </div>

    <div class="hr-chart-card hr-mb-4">
      <div class="hr-chart-body hr-py-3">
        <div class="mod-filters">
          <select v-model="filterStatus" class="hr-select" @change="loadCases">
            <option value="">All</option>
            <option value="open">Open</option>
            <option value="in_progress">In progress</option>
            <option value="closed">Closed</option>
          </select>
          <input v-model="search" type="search" class="hr-input" placeholder="Search..." @input="debouncedLoad" />
        </div>
      </div>
    </div>

    <div v-if="loading" class="hr-loading-wrap"><div class="hr-spinner"></div></div>
    <div v-else class="hr-table-wrapper">
      <table class="hr-table hr-table-hover">
        <thead>
          <tr><th>Case #</th><th>Employee</th><th>Category</th><th>Counselor</th><th>Priority</th><th>Status</th><th></th></tr>
        </thead>
        <tbody>
          <tr v-for="c in cases" :key="c.id">
            <td><code>{{ c.case_number }}</code></td>
            <td>{{ c.employee_name }}</td>
            <td>{{ c.category_name }}</td>
            <td>{{ c.counselor_name || '—' }}</td>
            <td>{{ c.priority }}</td>
            <td><span class="hr-badge hr-badge-info">{{ c.status }}</span></td>
            <td class="hr-text-end"><button type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="openDetail(c)">Open</button></td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Create -->
    <div v-if="showCreate" class="hr-modal-overlay" @click.self="showCreate = false">
      <div class="hr-modal-dialog hr-modal-lg">
        <div class="hr-modal-content">
          <div class="hr-modal-header"><h5>Open psychosocial case</h5><button class="hr-modal-close" @click="showCreate = false"><i class="bx bx-x"></i></button></div>
          <div class="hr-modal-body">
            <div class="hr-form-grid hr-form-grid-2">
              <div class="hr-field-full">
                <label class="hr-form-label">Employee *</label>
                <input v-model="empSearch" class="hr-input" @input="searchEmployees" placeholder="Search..." />
                <div v-if="empResults.length" class="mod-search-results">
                  <button v-for="e in empResults" :key="e.id" type="button" class="mod-search-item" @click="pickEmployee(e)">{{ e.fullName }}</button>
                </div>
                <p v-if="createForm.employeeName" class="hr-small hr-text-success">Selected: {{ createForm.employeeName }}</p>
              </div>
              <div>
                <label class="hr-form-label">Category *</label>
                <select v-model="createForm.categoryId" class="hr-select">
                  <option value="">Select...</option>
                  <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
                </select>
              </div>
              <div>
                <label class="hr-form-label">Counselor</label>
                <select v-model="createForm.counselorId" class="hr-select">
                  <option value="">Unassigned</option>
                  <option v-for="u in hrUsers" :key="u.userID" :value="u.userID">{{ u.names }}</option>
                </select>
              </div>
              <div>
                <label class="hr-form-label">Priority</label>
                <select v-model="createForm.priority" class="hr-select">
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div class="hr-field-full">
                <label class="hr-form-label">Description *</label>
                <textarea v-model="createForm.description" class="hr-input" rows="4" placeholder="Reason for referral (handle sensitively)"></textarea>
              </div>
            </div>
          </div>
          <div class="hr-modal-footer">
            <button class="hr-btn hr-btn-secondary" @click="showCreate = false">Cancel</button>
            <button class="hr-btn hr-btn-primary" :disabled="saving" @click="submitCreate">Open case</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Detail -->
    <div v-if="showDetail && detail" class="hr-modal-overlay" @click.self="closeDetail">
      <div class="hr-modal-dialog hr-modal-xl">
        <div class="hr-modal-content">
          <div class="hr-modal-header"><h5>{{ detail.case.case_number }}</h5><button class="hr-modal-close" @click="closeDetail"><i class="bx bx-x"></i></button></div>
          <div class="hr-modal-body">
            <p><strong>{{ detail.case.employee_name }}</strong> · {{ detail.case.category_name }} · Counselor: {{ detail.case.counselor_name || 'Unassigned' }}</p>
            <p class="hr-text-muted">{{ detail.case.description }}</p>

            <div class="mod-section">
              <h6>Sessions</h6>
              <ul v-if="detail.sessions.length">
                <li v-for="s in detail.sessions" :key="s.id">{{ formatDt(s.session_date) }} ({{ s.duration_minutes }} min, {{ s.session_type }})</li>
              </ul>
              <p v-else class="hr-text-muted hr-small">No sessions logged yet.</p>
              <div class="hr-form-grid hr-form-grid-2 hr-mt-2">
                <input v-model="sessionForm.sessionDate" type="datetime-local" class="hr-input" />
                <input v-model="sessionForm.durationMinutes" type="number" class="hr-input" placeholder="Minutes" />
                <select v-model="sessionForm.sessionType" class="hr-select">
                  <option value="individual">Individual</option>
                  <option value="group">Group</option>
                  <option value="follow_up">Follow-up</option>
                </select>
                <input v-model="sessionForm.nextSessionDate" type="date" class="hr-input" placeholder="Next session" />
                <textarea v-model="sessionForm.notes" class="hr-input hr-field-full" rows="2" placeholder="Session notes"></textarea>
              </div>
              <button type="button" class="hr-btn hr-btn-sm hr-btn-primary hr-mt-2" @click="addSession">Log session</button>
            </div>

            <div class="mod-section">
              <h6>Confidential notes</h6>
              <div v-for="n in detail.notes" :key="n.id" class="mod-note">
                <strong>{{ n.author_name }}</strong> <span class="hr-text-muted hr-small">{{ formatDt(n.created_at) }}</span>
                <p>{{ n.note }}</p>
              </div>
              <textarea v-model="noteForm.note" class="hr-input hr-mt-2" rows="2" placeholder="Add confidential note..."></textarea>
              <button type="button" class="hr-btn hr-btn-sm hr-btn-secondary hr-mt-2" @click="addNote">Add note</button>
            </div>

            <div v-if="detail.case.status !== 'closed'" class="mod-section">
              <button type="button" class="hr-btn hr-btn-success" @click="closeCase">Close case</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, toast } from "@/api";

export default {
  name: "PsychosocialManagement",
  components: { HrPageLayout },
  data() {
    return {
      loading: false, saving: false, cases: [], metrics: {}, categories: [], hrUsers: [],
      filterStatus: "", search: "", debounceTimer: null,
      showCreate: false, showDetail: false, detail: null,
      empSearch: "", empResults: [],
      createForm: { employeeId: "", employeeName: "", categoryId: "", description: "", priority: "normal", counselorId: "" },
      sessionForm: { sessionDate: "", durationMinutes: 60, sessionType: "individual", notes: "", nextSessionDate: "" },
      noteForm: { note: "" },
    };
  },
  mounted() {
    this.loadMeta();
    this.loadCases();
  },
  methods: {
    debouncedLoad() { clearTimeout(this.debounceTimer); this.debounceTimer = setTimeout(() => this.loadCases(), 350); },
    async loadMeta() {
      const catD = new FormData(); catD.append("function", "getPsychosocialCategories");
      const catRes = await execute(catD, authUrl);
      if (catRes?.data?.success) this.categories = catRes.data.data?.categories || [];
      const userD = new FormData(); userD.append("function", "getHrModuleUsers");
      const uRes = await execute(userD, authUrl);
      if (uRes?.data?.success) this.hrUsers = uRes.data.data?.users || [];
    },
    async loadCases() {
      this.loading = true;
      const d = new FormData(); d.append("function", "getPsychosocialCases");
      if (this.filterStatus) d.append("status", this.filterStatus);
      if (this.search) d.append("search", this.search);
      try {
        const res = await execute(d, authUrl);
        if (res?.data?.success) {
          this.cases = res.data.data?.cases || [];
          this.metrics = res.data.data?.metrics || {};
        }
      } finally { this.loading = false; }
    },
    openCreate() {
      this.createForm = { employeeId: "", employeeName: "", categoryId: "", description: "", priority: "normal", counselorId: "" };
      this.showCreate = true;
    },
    async searchEmployees() {
      const d = new FormData(); d.append("function", "searchEmployeesForHrModule"); d.append("q", this.empSearch);
      const res = await execute(d, authUrl);
      this.empResults = res?.data?.data?.employees || [];
    },
    pickEmployee(e) { this.createForm.employeeId = e.id; this.createForm.employeeName = e.fullName; this.empResults = []; },
    async submitCreate() {
      if (!this.createForm.employeeId || !this.createForm.categoryId || !this.createForm.description) {
        toast.error("Fill required fields"); return;
      }
      this.saving = true;
      const d = new FormData(); d.append("function", "createPsychosocialCase");
      ["employeeId", "categoryId", "description", "priority", "counselorId"].forEach(k => { if (this.createForm[k]) d.append(k, this.createForm[k]); });
      try {
        const res = await execute(d, authUrl);
        if (res?.data?.success) { toast.success("Case opened"); this.showCreate = false; this.loadCases(); }
        else toast.error(res?.data?.message);
      } finally { this.saving = false; }
    },
    async openDetail(c) {
      const d = new FormData(); d.append("function", "getPsychosocialCaseDetail"); d.append("id", c.id);
      const res = await execute(d, authUrl);
      if (res?.data?.success) { this.detail = res.data.data; this.showDetail = true; }
    },
    closeDetail() { this.showDetail = false; this.detail = null; this.loadCases(); },
    async post(fn, fields) {
      const d = new FormData(); d.append("function", fn);
      Object.entries(fields).forEach(([k, v]) => { if (v != null && v !== "") d.append(k, v); });
      const res = await execute(d, authUrl);
      if (res?.data?.success) { toast.success("Saved"); await this.openDetail({ id: this.detail.case.id }); return true; }
      toast.error(res?.data?.message); return false;
    },
    addSession() { this.post("addPsychosocialSession", { caseId: this.detail.case.id, ...this.sessionForm }); },
    addNote() { this.post("addPsychosocialNote", { caseId: this.detail.case.id, note: this.noteForm.note, isConfidential: 1 }); this.noteForm.note = ""; },
    closeCase() { this.post("closePsychosocialCase", { id: this.detail.case.id }); },
    formatDt(v) { return v ? new Date(v).toLocaleString() : ""; },
  },
};
</script>

<style scoped>
.mod-filters { display: flex; gap: 12px; flex-wrap: wrap; }
.mod-search-results { border: 1px solid #e2e8f0; border-radius: 8px; max-height: 140px; overflow: auto; }
.mod-search-item { display: block; width: 100%; padding: 8px 12px; border: none; background: #fff; text-align: left; cursor: pointer; }
.mod-section { margin-top: 20px; padding-top: 16px; border-top: 1px solid #e2e8f0; }
.mod-note { background: #f8fafc; padding: 8px; border-radius: 6px; margin-bottom: 8px; }
.hr-modal-xl { max-width: 900px; width: 95%; }
</style>
